(() => {
  if (customElements.get('jeele-warranty')) return;
  class JeeleWarranty extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.mode = 'product';
      this.events = new AbortController();
      this.blobs = [];
      this.sequence = 0;
      this.form = this.querySelector('.jw-form');
      this.result = this.querySelector('.jw-result');
      this.status = this.querySelector('.jw-status');
      try { this.text = JSON.parse(this.querySelector('[data-jw-text]').textContent); }
      catch { return; }
      this.setMode('product');
      const eventOptions = {signal:this.events.signal};
      this.querySelectorAll('[data-mode]').forEach((button) => button.addEventListener('click', () => this.setMode(button.dataset.mode), eventOptions));
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.lookup(); }, eventOptions);
      this.form.elements.code.addEventListener('input', () => this.clear(false), eventOptions);
      this.form.elements.access_key.addEventListener('input', () => this.clear(false), eventOptions);
      this.querySelector('.jw-clear').addEventListener('click', () => { this.clear(); this.form.reset(); this.form.elements.code.focus(); }, eventOptions);
      window.addEventListener('pagehide', () => { this.clear(); this.form.elements.access_key.value = ''; }, eventOptions);
      this.addEventListener('shopify:block:select', (event) => {
        const details = event.target.closest('details');
        if (details && this.contains(details)) details.open = true;
      }, eventOptions);
    }
    disconnectedCallback() {
      if (this.form && this.text) {
        this.clear();
        this.form.elements.access_key.value = '';
      }
      this.events?.abort(); this.request?.abort();
      this.blobs?.forEach((url) => URL.revokeObjectURL(url));
      this.initialized = false;
    }
    setMode(mode) {
      this.clear(); this.mode = mode;
      this.querySelectorAll('[data-mode]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.mode === mode)));
      this.querySelector('.jw-key').hidden = mode !== 'warranty';
      this.form.elements.access_key.required = mode === 'warranty';
      if (mode === 'product') this.form.elements.access_key.value = '';
      this.form.querySelector('[type=submit]').textContent = mode === 'product' ? this.text.check_product : this.text.view_details;
    }
    clear(clearStatus = true) {
      this.sequence++;
      this.request?.abort();
      this.request = null;
      this.result.hidden = true;
      this.querySelector('.jw-product-fields').replaceChildren();
      this.querySelector('.jw-installation').replaceChildren();
      this.blobs.forEach((url) => URL.revokeObjectURL(url)); this.blobs = [];
      if (clearStatus) { this.status.hidden = true; this.status.textContent = ''; }
      if (this.form) {
        this.form.removeAttribute('aria-busy');
        this.form.querySelector('[type=submit]').disabled = false;
        this.form.querySelector('[type=submit]').textContent = this.mode === 'product' ? this.text.check_product : this.text.view_details;
      }
    }
    message(text, error = false) {
      this.status.textContent = text; this.status.hidden = false;
      this.status.dataset.error = String(error);
    }
    origin() {
      const origin = this.dataset.api?.replace(/\/$/, '');
      if (!origin) throw new Error(this.text.service_unavailable);
      let url;
      try { url = new URL(origin); } catch { throw new Error(this.text.service_unavailable); }
      if ((url.protocol !== 'https:' && !(url.protocol === 'http:' && ['localhost','127.0.0.1'].includes(url.hostname))) || url.pathname !== '/' || url.username || url.password || url.search || url.hash) throw new Error(this.text.service_unavailable);
      return url.origin;
    }
    async lookup() {
      this.clear();
      const sequence = this.sequence, code = this.form.elements.code.value.trim();
      const accessKey = this.mode === 'warranty' ? this.form.elements.access_key.value.trim() : '';
      this.form.elements.code.removeAttribute('aria-invalid');
      this.form.elements.access_key.removeAttribute('aria-invalid');
      if (!/^[a-zA-Z0-9][a-zA-Z0-9._/\-]{0,79}$/.test(code)) {
        this.message(code ? this.text.invalid_code : this.text.enter_code,true);
        this.form.elements.code.setAttribute('aria-invalid','true'); this.form.elements.code.focus(); return;
      }
      if (this.mode === 'warranty' && !accessKey) {
        this.message(this.text.enter_key,true); this.form.elements.access_key.setAttribute('aria-invalid','true'); this.form.elements.access_key.focus(); return;
      }
      const button = this.form.querySelector('[type=submit]');
      button.disabled = true; button.textContent = this.text.loading;
      this.form.setAttribute('aria-busy','true');
      const controller = new AbortController(); this.request = controller;
      const timeout = setTimeout(() => controller.abort('timeout'), 12000);
      try {
        const origin = this.origin();
        const response = await fetch(`${origin}/api/public/lookup`, {method:'POST',credentials:'omit',cache:'no-store',referrerPolicy:'no-referrer',headers:{'Content-Type':'application/json'},body:JSON.stringify({code,access_key:accessKey}),signal:controller.signal});
        if (sequence !== this.sequence || !this.isConnected) return;
        if (!response.ok) {
          const errors = {404:this.text.not_found,403:this.text.verification_failed,422:this.text.invalid_code,429:this.text.rate_limited};
          throw new Error(errors[response.status] || this.text.service_error);
        }
        const data = await response.json();
        if (!data.product || !['installed','not_installed'].includes(data.state) || (accessKey && !data.verified)) throw new Error(this.text.service_error);
        this.render(data);
        this.message(this.text.found);
        this.result.hidden = false;
        this.result.querySelector('h2').focus({preventScroll:true});
        this.result.scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',block:'start'});
        if (data.installation?.photos?.length && data.verified) await this.loadPhotos(data.installation.photos,{code,access_key:accessKey},origin,sequence);
      } catch (error) {
        if (sequence !== this.sequence || !this.isConnected) return;
        if (controller.signal.aborted && controller.signal.reason !== 'timeout') return;
        this.message(controller.signal.aborted ? this.text.timeout : error instanceof TypeError ? this.text.service_error : error.message,true);
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence && this.isConnected) {
          this.form.removeAttribute('aria-busy'); button.disabled = false;
          button.textContent = this.mode === 'product' ? this.text.check_product : this.text.view_details;
        }
      }
    }
    node(tag,text,className) {
      const element = document.createElement(tag);
      if (text != null) element.textContent = text;
      if (className) element.className = className;
      return element;
    }
    fields(container,values) {
      values.forEach(([label,value]) => {
        const field = this.node('div');
        field.append(this.node('dt',label),this.node('dd',value || this.text.not_confirmed)); container.append(field);
      });
    }
    render(data) {
      const text = this.text, product = data.product;
      this.querySelector('.jw-result-state').textContent = data.state === 'not_installed' ? text.not_installed : data.verified ? text.verified : text.installed_private;
      this.fields(this.querySelector('.jw-product-fields'),[[text.model,product.model],[text.product_code,product.code],[text.size,product.size],[text.production_date,product.production_date],[text.color,product.color]]);
      const installation = this.querySelector('.jw-installation');
      installation.hidden = !data.installation || !data.verified;
      if (data.installation && data.verified) {
        const record = data.installation, fields = this.node('dl',null,'jw-record-fields');
        installation.append(this.node('h3',text.installation_information),fields);
        this.fields(fields,[[text.customer_name,record.customer_name],[text.vehicle,record.vehicle],[text.coverage,record.coverage],[text.phone,record.phone],[text.vin,record.vin],[text.plate,record.plate],[text.installed_on,record.installed_on],[text.install_shop,record.install_shop],[text.technician,record.technician],[text.remark,record.remark]].filter(([,value]) => value));
        this.fields(fields,[[text.policy_reference,record.policy_reference],[text.warranty_start,record.warranty_start],[text.warranty_end,record.warranty_end]]);
        if (record.photos.length) installation.append(this.node('h3',text.installation_photos),this.node('div',null,'jw-photo-grid'));
      }
      if (data.state === 'installed' && !data.verified) {
        const button = this.node('button',text.view_details,'jw-button'); button.type = 'button';
        button.addEventListener('click',() => { this.setMode('warranty'); this.form.elements.access_key.focus(); },{signal:this.events.signal});
        this.querySelector('.jw-installation').append(button); this.querySelector('.jw-installation').hidden = false;
      }
    }
    async loadPhotos(ids,payload,origin,sequence) {
      const grid = this.querySelector('.jw-photo-grid');
      if (!grid) return;
      for (const [index,id] of ids.entries()) {
        if (sequence !== this.sequence) return;
        const figure = this.node('figure'), caption = this.node('p',`${this.text.photo} ${index+1}`);
        figure.append(caption); grid.append(figure);
        const load = async () => {
          const controller = new AbortController(), timer = setTimeout(() => controller.abort(),30000);
          const abort = () => controller.abort();
          this.events.signal.addEventListener('abort',abort,{once:true});
          try {
            const response = await fetch(`${origin}/api/public/photos/${encodeURIComponent(id)}`,{method:'POST',credentials:'omit',cache:'no-store',referrerPolicy:'no-referrer',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload),signal:controller.signal});
            if (!response.ok) throw new Error();
            const blob = await response.blob();
            if (sequence !== this.sequence || !this.isConnected) return;
            const url = URL.createObjectURL(blob); this.blobs.push(url);
            const image = this.node('img'); image.src = url; image.alt = `${this.text.installation_photos} ${index+1}`;
            const link = this.node('a'); link.href = url; link.target = '_blank'; link.rel = 'noopener'; link.append(image);
            figure.replaceChildren(link,caption);
          } catch {
            if (sequence !== this.sequence || !this.isConnected) return;
            const retry = this.node('button',this.text.photo_retry,'jw-clear'); retry.type = 'button';
            retry.addEventListener('click',() => { retry.disabled = true; load(); },{signal:this.events.signal});
            figure.replaceChildren(caption,this.node('p',this.text.photo_error),retry);
          } finally { clearTimeout(timer); this.events.signal.removeEventListener('abort',abort); }
        };
        await load();
      }
    }
  }
  customElements.define('jeele-warranty',JeeleWarranty);
})();
